import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { 
  Room, 
  RoomOptions, 
  VideoCodec, 
  VideoPresets,
  RoomEvent
} from 'livekit-client';
import { LocalUserChoices } from '@livekit/components-react';
import { startStreamingSession, incrementAvatarRequestCounter, deleteUserPreviousRoomAction, detectHead, preLaunchRunpod, StartLLMBackend } from '@/app/lib/actions';
import { sendSessionStartToDiscord, sendLoadingProgressToDiscord, sendStreamStartedToDiscord } from '@/app/lib/actions/discord';
import { generateRoomId } from '@/lib/client-utils';
import { ConnectionDetails } from '@/lib/types';
import type { Character } from '@/app/lib/types/character';
import { ChatStatus } from '../data/globalChatState';
import { useActiveImage, useCharacterData, useChatStatus, useGlobalChatState, useGlobalLiveKitRoomState } from './useGlobalChatState';
import { checkUserAssignedRoomAction, findIdleRoomAction, findInitRoomAction, assignUserToRoomAction, setRoomStateAction, checkUserRoomIdleAction, checkRoomAndAgentAvatarAction } from '@/app/lib/actions/room';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useDevice } from '@/app/lib/contexts/DeviceContext';
import { initiatePrewarmAction } from '@/app/lib/actions/room-gpu-match-actions';

const CONN_DETAILS_ENDPOINT = process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT ?? '/api/connection-details';

// Helper function to generate random room names like "mxbc-dkru"
const generateRandomRoomName = (): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz';
  const generatePart = () => Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${generatePart()}-${generatePart()}`;
};

export function useVideoStream() {
  const { globalLKRoomState, globalLKRoomStateActions } = useGlobalLiveKitRoomState();
  const { chatStatus, setChatStatus } = useChatStatus();
  const [globalState, globalStateActions] = useGlobalChatState();
  // const [room, setRoom] = useState<Room | null>(null);
  // const [isInitiating, setIsInitiating] = useState(false);
  // const [preJoinChoices, setPreJoinChoices] = useState<LocalUserChoices | undefined>(undefined);
  // const [connectionDetails, setConnectionDetails] = useState<ConnectionDetails | undefined>(undefined);
  
  const { session, userEmail, userName } = useAuth();
  const { character: avatar } = useCharacterData();
  const { imageUri: activeImageUri } = useActiveImage();
  const [userAppDataState] = useUserAppData(userEmail || '');
  const userId = userAppDataState.userId;
  const { isMobile, isDesktop } = useDevice();
  
  // Simple loading timer refs
  const loadingStartTimeRef = useRef<number | null>(null);
  const loadingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Refs for agent signals and room - single source of truth for the interval loop
  const hasAgentAvatarSignalRef = useRef<boolean>(false);
  const hasAgentAsrSignalRef = useRef<boolean>(false);
  const roomRef = useRef<Room | null>(null);
  // Ref to store latest chatStatus from hook to avoid closure issues in interval callback
  const chatStatusRef = useRef<ChatStatus>(chatStatus);
  
  // State for connection issue popup
  const [showConnectionIssuePopup, setShowConnectionIssuePopup] = useState(false);
  
  // Keep ref in sync with hook value
  useEffect(() => {
    chatStatusRef.current = chatStatus;
  }, [chatStatus]);

  const preJoinDefaults = useMemo(() => {
    return {
      username: userName || 'user',
      videoEnabled: false,
      audioEnabled: true,
      videoDeviceId: undefined,
      audioDeviceId: undefined,
    };
  }, [userName]);


  const initiateVideoRoom = useCallback(async () => {
    // updated: 10/19/2025, now only video related logic is handled in here.
    // we consider these cases:
    // only case used: launching live video, either method 2 or 3
    // we also have several connection methods for video with gpu: method 2, reuse idle room, method 3, create a new room

    // --- 1. determine the case type ---
    // Use singleton to avoid closure-captured stale values
    setChatStatus(ChatStatus.LOADING);
    
    // --- 2. prepare the process, launch timer ---
    if (!avatar || !session || globalLKRoomState.isInitiatingRoom) return;   
    globalLKRoomStateActions.setIsInitiatingRoom(true);
    // Reset agent signals when starting a new session
    globalStateActions.resetAgentSignals();
    hasAgentAvatarSignalRef.current = false;
    hasAgentAsrSignalRef.current = true;
    
    if (true) {
      // Start simple loading timer for Discord updates and video/agent checking
      loadingStartTimeRef.current = Date.now();
      let lastSentTime = 0;
      
      loadingIntervalRef.current = setInterval(() => {
        if (!loadingStartTimeRef.current || !avatar || !userId) return;

        const elapsedSeconds = Math.floor((Date.now() - loadingStartTimeRef.current) / 1000);

        // Skip sending and break loop if loading takes longer than 60 seconds
        if (elapsedSeconds > 60) {
          console.error('Loading timeout after 60 seconds - resetting states');
          
          // Clear the loading timer
          if (loadingIntervalRef.current) {
            clearInterval(loadingIntervalRef.current);
            loadingIntervalRef.current = null;
          }
          loadingStartTimeRef.current = null;
          
          // Reset initiating state
          globalLKRoomStateActions.setIsInitiatingRoom(false);
          
          // Disconnect and reset room states
          if (roomRef.current) {
            roomRef.current.disconnect().catch(() => {});
          }
          if (globalLKRoomState.room) {
            globalLKRoomState.room.disconnect().catch(() => {});
            globalLKRoomStateActions.setRoom(null);
          }
          roomRef.current = null;
          globalLKRoomStateActions.setConnectionDetails(undefined);
          globalLKRoomStateActions.setPreJoinChoices(undefined);
          
          // Reset agent signals
          globalStateActions.resetAgentSignals();
          hasAgentAvatarSignalRef.current = false;
          hasAgentAsrSignalRef.current = false;
          
          // Reset chat status to INFO
          setChatStatus(ChatStatus.INFO);
          
          // Show connection issue popup
          setShowConnectionIssuePopup(true);
          
          return;
        }

        // Check for video element and agent-avatar role
        const checkForVideoAndAgent = () => {
          // const videoElement = document.querySelector('.lk-participant-media-video[data-lk-local-participant="false"]');
          const videoElement = true;
          const hasAgentAvatarSignal = hasAgentAvatarSignalRef.current;
          // const hasAgentAsrSignal = hasAgentAsrSignalRef.current;
          const hasAgentAsrSignal = true;
          hasAgentAsrSignalRef.current = true;
          const hasAgentAsrParticipant = true;
          
          // Detailed logging for hasAgentAvatarParticipant
          const remoteParticipants = Array.from(roomRef.current?.remoteParticipants.values() || []);
          const hasAgentAvatarParticipant = remoteParticipants.some(participant => {
            if (participant.attributes?.role !== 'agent-avatar') return false;
            return true;
          });

          // Check for agent-asr participant and set ASR signal if found
          // const hasAgentAsrParticipant = remoteParticipants.some(participant => {
          //   if (participant.attributes?.role !== 'agent-asr') return false;
          //   return true;
          // });

          // Set ASR signal if we found an agent-asr participant
          // We need this on top of the agent-asr-enter signal, as the agent-asr-enter signal may be sent before
          // that part of code is reached.
          if (hasAgentAsrParticipant) {
            globalStateActions.setAgentAsrEntered(true);
            hasAgentAsrSignalRef.current = true;
            console.log(`InitDebug: Agent-ASR participant detected, setting hasAgentAsrSignal to true`);
          }
          
          // Log detailed information every 1 second
          console.log('=== hasAgentAvatarParticipant Debug Info ===');
          console.log('Chat Status (from ref):', chatStatusRef.current);
          console.log('Chat Status (from hook/closure):', chatStatus);
          console.log('Room State:', roomRef.current?.state);
          console.log('Total Remote Participants:', remoteParticipants.length);
          console.log('Remote Participants Details:', remoteParticipants.map(p => ({
            identity: p.identity,
            sid: p.sid,
            attributes: p.attributes,
            role: p.attributes?.role,
            isAgentAvatar: p.attributes?.role === 'agent-avatar',
            isAgentAsr: p.attributes?.role === 'agent-asr'
          })));
          console.log('hasAgentAvatarParticipant:', hasAgentAvatarParticipant);
          console.log('hasAgentAsrParticipant:', hasAgentAsrParticipant);
          console.log('hasAgentAvatarSignal:', hasAgentAvatarSignal);
          console.log('hasAgentAsrSignal:', hasAgentAsrSignal);
          console.log('videoElement:', videoElement);
          console.log('All conditions met:', videoElement && hasAgentAvatarSignal && hasAgentAsrSignal && hasAgentAvatarParticipant);
          // CRITICAL: Read from ref to avoid closure-captured stale value from hook
          const currentStatus = chatStatusRef.current;
          console.log('Current chatStatus from ref:', currentStatus);
          console.log('Current chatStatus from hook (closure):', chatStatus);
          console.log('==========================================');
          
          // Check current chat status from ref, not closure-captured hook value
          if (videoElement && hasAgentAvatarSignal && hasAgentAsrSignal && hasAgentAvatarParticipant && currentStatus === ChatStatus.LOADING) {
            console.log('🎉 All conditions met! Setting chat status to LIVE');
            
            // Send final Discord message with total loading time (1 decimal place)
            if (loadingStartTimeRef.current && avatar && userId) {
              const totalLoadingTime = Math.round((Date.now() - loadingStartTimeRef.current) / 100) / 10;
              sendStreamStartedToDiscord(avatar.metadata.avatar_id, userId, totalLoadingTime, whichMethod);
            }
            
            // Clear the loading timer
            if (loadingIntervalRef.current) {
              clearInterval(loadingIntervalRef.current);
              loadingIntervalRef.current = null;
            }
            loadingStartTimeRef.current = null;
            
            setChatStatus(ChatStatus.LIVE);
          }
          
          return { hasAgentAvatarSignal, hasAgentAsrSignal, hasAgentAvatarParticipant };
        };
        
        // Run the check
        const signals = checkForVideoAndAgent();

        // Send Discord update every 2 seconds after 10 seconds
        if (elapsedSeconds > 10 && elapsedSeconds % 2 === 0 && elapsedSeconds !== lastSentTime && signals) {
          lastSentTime = elapsedSeconds;
          sendLoadingProgressToDiscord(
            avatar.metadata.avatar_id,
            userId,
            elapsedSeconds,
            whichMethod,
            signals.hasAgentAvatarSignal,
            signals.hasAgentAsrSignal,
            signals.hasAgentAvatarParticipant
          );
        }
      }, 1000);
    }

    // --- 3. Find out the room to use ---
    // --- Then launch both llm and gpu ---
    let hasRoomName = false;
    let roomName = '';
    let whichMethod = -1;

    const startTime = Date.now();
    if (true) {
      // we check if there is an idle room, if so, use this room
      if (!hasRoomName) {
        // if found, the state is immediately set to busy, acting like reserving the room.
        const availableRoomResult = await findIdleRoomAction();
        if (availableRoomResult.success && availableRoomResult.roomId) {
          roomName = availableRoomResult.roomId;
          hasRoomName = true;
          whichMethod = 2;
        }
      }
      console.log(`InitDebug: After findIdleRoom, hasRoomName: ${hasRoomName}, roomName: ${roomName}, whichMethod: ${whichMethod}, time: ${Date.now() - startTime}ms`);
      // Init room is not stable. Only check idle room.
      // if (!hasRoomName) {
      //   const availableRoomResult = await findInitRoomAction();
      //   if (availableRoomResult.success && availableRoomResult.roomId) {
      //     roomName = availableRoomResult.roomId;
      //     hasRoomName = true;
      //     whichMethod = 0;
      //   }
      // }

      // For method 2, let's check if the room still exist and if the agent-avatar is in there.
      if (whichMethod === 2)
      {
        const roomCheckResult = await checkRoomAndAgentAvatarAction(roomName);
        const reuseRoomSuccess = roomCheckResult.success && roomCheckResult.roomExists && roomCheckResult.hasAgentAvatar;
        if (!reuseRoomSuccess) {
          // flip the reuse room state back to idle
          setRoomStateAction(roomName, 'idle');
          // If we can't check the room, fall back to method 3
          hasRoomName = false;
          roomName = '';
          whichMethod = 3;
        }
      }
      console.log(`InitDebug: After checkRoomAndAgentAvatar, hasRoomName: ${hasRoomName}, roomName: ${roomName}, whichMethod: ${whichMethod}, time: ${Date.now() - startTime}ms`);

      // generate a random room name for method 3
      if (!hasRoomName) {
        roomName = generateRandomRoomName();
        whichMethod = 3;
      }

      // mark it as busy, as this user is using it. 
      // if joining an idle room, it will be closed by the server.
      setRoomStateAction(roomName, 'busy')
      console.log(`InitDebug: Marking room ${roomName} as busy, time: ${Date.now() - startTime}ms`);
    }

    // --- 4. launch llm ---
    // const llmLaunchResult = await StartLLMBackend({
    //   room: roomName,
    //   llm_option: globalState.userChatSetting.languageModel,
    //   image_gen_option: globalState.userChatSetting.imageGenMode,
    //   avatar_id: avatar.metadata.avatar_id,
    //   ttsVoiceIdCartesia: globalState.userChatSetting.ttsId,
    //   userEmail: userEmail || '',
    // });

    // --- 5. launch gpu ---
    if (true) {
        // Get current chat settings from global state
        const currentChatSettings = globalState.userChatSetting;
        
        // Start streaming session
        const sessionResponse = await startStreamingSession({
          instruction: "test",
          seconds: 86400,
          room: roomName,
          avatarSource: activeImageUri || '',
          avatar_id: avatar.metadata.avatar_id,
          llm_option: currentChatSettings.languageModel,
          image_gen_option: currentChatSettings.imageGenMode,
          llmUserNickname: userName || 'Friend',
          llmUserBio: 'a friend',
          llmAssistantNickname: avatar.metadata.avatar_name,
          llmAssistantBio: avatar.metadata.agent_bio || 'this is an agent bio',
          llmAssistantAdditionalCharacteristics: avatar.metadata.prompt || '',
          llmConversationContext: avatar.metadata.scene_prompt,
          ttsVoiceIdCartesia: currentChatSettings.ttsId,
          userEmail: userEmail || '',
          launch_gpu: true,
          whichMethod: whichMethod,
        });
        console.log(`InitDebug: After startStreamingSession, time: ${Date.now() - startTime}ms`);
    }

    // --- 6. setup LiveKit connection in frontend ---

    try {
      // Setup LiveKit connection
      globalLKRoomStateActions.setPreJoinChoices(preJoinDefaults);

      const url = new URL(CONN_DETAILS_ENDPOINT, window.location.origin);
      url.searchParams.append('roomName', roomName);
      url.searchParams.append('participantName', preJoinDefaults.username);
      
      const connectionDetailsResp = await fetch(url.toString());
      const connectionDetailsData = await connectionDetailsResp.json();
      globalLKRoomStateActions.setConnectionDetails(connectionDetailsData);

      const roomOptions: RoomOptions = {
        videoCaptureDefaults: {
          deviceId: preJoinDefaults.videoDeviceId ?? undefined,
          resolution: VideoPresets.h720,
        },
        publishDefaults: {
          dtx: false,
          videoSimulcastLayers: [VideoPresets.h540, VideoPresets.h216],
          red: true,
          videoCodec: 'VP8' as VideoCodec,
        },
        audioCaptureDefaults: {
          deviceId: preJoinDefaults.audioDeviceId ?? undefined,
        },
        adaptiveStream: { pixelDensity: 'screen' },
        dynacast: true,
      };

      const newRoom = new Room(roomOptions);
      
      // Remove any existing listeners before adding new ones
      newRoom.removeAllListeners?.();
      
      await newRoom.connect(connectionDetailsData.serverUrl, connectionDetailsData.participantToken);
      console.log(`InitDebug: After frontend connect, time: ${Date.now() - startTime}ms`);
      // if the room is old (either assigned to this user or belong to other users), we need to send the signal to swap server.
      if (whichMethod === 2) {
        const messageData = {
          topic: 'room_signal',
          type: 'how_swap_server',
          image_uri: activeImageUri,
          voice_id: globalState.userChatSetting.ttsId,
        };
        
        await newRoom.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(messageData)),
          { reliable: true }
        );
      }
      console.log(`InitDebug: After send how_swap_server, time: ${Date.now() - startTime}ms`);
      
      // Set up room-level event listeners
      newRoom.on(RoomEvent.DataReceived, (payload: Uint8Array, participant: any) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          
          // Handle serverless_broken signal at room level
          if (data.topic === 'frontend_stream' && data.type === 'serverless_broken') {
            console.log('Received serverless_broken signal at room level, handling gracefully');
            if (newRoom && newRoom.state === 'connected') {
              try {
                newRoom.disconnect();
                console.log('Successfully disconnected from room due to serverless_broken signal');
                
                // Show connection issue popup instead of refreshing the page
                setShowConnectionIssuePopup(true);
                
                // Smoothly transition to INFO state instead of refreshing
                setChatStatus(ChatStatus.INFO);
                
              } catch (error) {
                console.error('Error disconnecting from room:', error);
              }
            }
          }
          
          // Handle agent avatar enter signal
          if (data.topic === 'room_signal' && data.type === 'agent-avatar-enter') {
            globalStateActions.setAgentAvatarEntered(true);
            hasAgentAvatarSignalRef.current = true;
            console.log(`InitDebug: After agent-avatar-enter, time: ${Date.now() - startTime}ms`);
          }
          
          // Handle agent ASR enter signal
          // Update: agent-asr-enter could arrive before this code is reached.
          // if (data.topic === 'room_signal' && data.type === 'agent-asr-enter') {
          //   globalStateActions.setAgentAsrEntered(true);
          //   hasAgentAsrSignalRef.current = true;
          //   console.log(`InitDebug: After agent-asr-enter, time: ${Date.now() - startTime}ms`);
          // }
          
        } catch (error) {
          console.error('Error processing room-level data:', error);
        }
      });
      
      globalLKRoomStateActions.setRoom(newRoom);
      roomRef.current = newRoom; // Update ref so interval can access it
      // post room start operations
      // warming up the valid head detect lambda
      detectHead('lol empty image', `pre-streaming warmup`, false);
      // send session start webhook to discord
      const deviceType = isMobile ? 'mobile' : 'desktop';

      sendSessionStartToDiscord(avatar.metadata.avatar_id, userId, deviceType, whichMethod, userEmail);
      
      console.log(`InitDebug: After send session start to discord, time: ${Date.now() - startTime}ms`);
    } catch (error) {
      console.error('Failed to start video chat:', error);
      
      // Clear loading timer on error
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
      loadingStartTimeRef.current = null;
      roomRef.current = null;
      
      throw error;
    } finally {
      globalLKRoomStateActions.setIsInitiatingRoom(false);
    }
  }, [avatar, session, globalLKRoomStateActions, setChatStatus, globalStateActions]);

  // Cleanup effect when leaving video mode or unmounting
  useEffect(() => {
    if (chatStatus === ChatStatus.INFO) {
      if (globalLKRoomState.room) {
        // best-effort await; use an IIFE because effects can't be async
        (async () => {
          try { 
            // if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
            //   // Send llm_leave signal to Rita LLM backend before disconnecting
            //   const leaveSignal = {
            //     topic: "room_signal",
            //     type: "llm_leave",
            //   };
            //   try {
            //     await globalLKRoomState.room.localParticipant.publishData(
            //       new TextEncoder().encode(JSON.stringify(leaveSignal)),
            //       { reliable: true }
            //     );
            //     console.log('Sent llm_leave signal to backend');
            //     // Wait a bit to ensure signal is sent before disconnecting
            //     await new Promise(resolve => setTimeout(resolve, 100));
            //   } catch (error) {
            //     console.error('Error sending llm_leave signal:', error);
            //   }
              
            //   await globalLKRoomState.room.disconnect(); 
            // }
          } catch {}
          globalLKRoomStateActions.setRoom(null);
        })();
      }
      globalLKRoomStateActions.setConnectionDetails(undefined); // Clear connection details
      globalLKRoomStateActions.setPreJoinChoices(undefined);    // Clear pre-join choices
      // Reset agent signals when leaving video mode
      globalStateActions.resetAgentSignals();
      hasAgentAvatarSignalRef.current = false;
      hasAgentAsrSignalRef.current = false;
      roomRef.current = null;
      
      // Clear loading timer when leaving video mode
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
      loadingStartTimeRef.current = null;
      
      setChatStatus(ChatStatus.INFO);
    }
  }, [chatStatus, globalLKRoomState.room, globalLKRoomStateActions, globalStateActions]);

  
  return {
    showConnectionIssuePopup,
    setShowConnectionIssuePopup,
    initiateVideoRoom, // Expose for explicit calling
  };
} 